'use client';

import { defaultSeo } from '@constants/seo';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import {
  getCookieBasketCount,
  getCookieWckAuthData,
  getCustomAdAgreeYn,
} from '@wconcept/wconcept-common/utils/cookie';
import { compress, decompress } from 'lz-string';
import {
  EncodeAssistData,
  DecodeAssistData,
  AppInfo,
  PageInfo,
  SeoMetaType,
  WckAuthCookie,
} from '@wconcept/wconcept-common/models/display/common';
import useResize from '@wconcept/wconcept-ui-renewal/src/hooks/useResize';
import MarkeingScriptTags from '@components/common/MarkeingScriptTags';
import type { DatadogProperty } from '@wconcept/wconcept-common/models/renewal/datadog';

export interface IPagePropsState<T> {
  deviceInfo: {
    device: 'iOS' | 'PC' | 'Andriod' | 'Mobile' | string;
    version: string;
    userAgent: string;
  };
  assistData: EncodeAssistData;
  initialData: T;
  mode: string | undefined;
  myHeartList: {
    product: Set<string>;
    contents: Set<{ contentType: string; contentId: string }>;
    brand: Set<string>;
  };
  myFollowList: {
    followIds: Set<number>;
  };
  appInfo: AppInfo;
  wckAuthData: WckAuthCookie;
  isStatic: boolean;
  pageInfo: PageInfo;
  seoMeta: SeoMetaType;
  isMobile: boolean;
  domainGender: 'Women' | 'Men' | 'All';
  datadogProperty?: DatadogProperty;
  addMyHeartProductItem: (itemCd: string) => void;
  deleteMyHeartProdcutItem: (itemCd: string) => void;
  addMyHeartBrandItem: (brandCd: string) => void;
  deleteMyHeartBrandItem: (brandCd: string) => void;
  findContent: (param: { contentId: string; contentType: string }) => boolean;
  addMyHeartContentItem: (content: {
    contentType: string;
    contentId: string;
  }) => void;
  deleteMyHeartContentItem: (itemCd: {
    contentType: string;
    contentId: string;
  }) => void;
  setProductMyHeart: (list: string[]) => void;
  setBrandMyHeart: (list: string[]) => void;
  setContentsMyHeart: (
    list: { contentType: string; contentId: string }[]
  ) => void;
  updateContentsMyHeart: (
    list: { contentType: string; contentId: string }[]
  ) => void;
  updateProductMyHeart: (list: string[]) => void;
  updateBrandMyHeart: (list: string[]) => void;
  setFollowIds: (list: number[]) => void;
  updateFollowIds: (list: number[]) => void;
  addFollowId: (param: number) => void;
  deleteFollowId: (param: number) => void;
}

// Default page props
export const defaultPageProps: IPagePropsState<undefined> = {
  deviceInfo: {
    device: '',
    version: '',
    userAgent: '',
  },
  wckAuthData: {} as WckAuthCookie,
  isMobile: true,
  assistData: {
    basketCount: '',
    isLogin: false,
    user: compress(
      JSON.stringify({
        userNo: '0',
        userId: '',
        userName: '',
        gender: '',
        grade: '8',
        birth: '',
        mobile: '',
        isCert: false,
        isPasswordChange: false,
        isAuthLogin: false,
        lastLoginDate: '',
        joinDate: '',
        lastOrderDate: '',
        snsType: '',
        loginType: '',
        smsYN: '',
        emailYN: '',
        pushYN: '',
        adYN: '',
        liveAlarmYN: '',
        isEmployee: '',
        cooperationCompanyCode: '',
      })
    ),
    customAdYn: '',
  },
  myHeartList: {
    product: new Set(),
    brand: new Set(),
    contents: new Set(),
  },
  myFollowList: {
    followIds: new Set(),
  },
  domainGender: 'Women',
  initialData: undefined,
  mode: process.env.NEXT_PUBLIC_MODE,
  appInfo: {
    isNativeAppBoth: false,
    isIPhoneXApp: false,
    isIos: false,
    isAndroid: false,
  },
  pageInfo: {
    pathname: '',
    href: '',
    returnUrl: '',
    refererUrl: '',
    adfrom: '',
  },
  isStatic: false,
  seoMeta: defaultSeo,
  addMyHeartProductItem: () => {},
  deleteMyHeartProdcutItem: () => {},
  addMyHeartBrandItem: () => {},
  deleteMyHeartBrandItem: () => {},
  addMyHeartContentItem: () => {},
  deleteMyHeartContentItem: () => {},
  setContentsMyHeart: () => {},
  setProductMyHeart: () => {},
  setBrandMyHeart: () => {},
  findContent: () => false,
  updateContentsMyHeart: () => {},
  updateProductMyHeart: () => {},
  updateBrandMyHeart: () => {},
  setFollowIds: () => {},
  updateFollowIds: () => {},
  addFollowId: () => {},
  deleteFollowId: () => {},
};

interface GlobalPagePropsContextState extends IPagePropsState<unknown> {
  decodedAssistData?: DecodeAssistData;
}

const GlobalPagePropsContext =
  createContext<GlobalPagePropsContextState>(defaultPageProps);

function GlobalPagePropsProvider({
  pageProps,
  children,
  datadogProperty,
}: React.PropsWithChildren<{
  pageProps?: IPagePropsState<unknown>;
  datadogProperty?: DatadogProperty;
}>) {
  const parentPageState = useContext(GlobalPagePropsContext);
  const [pageState, setPageState] = useState<IPagePropsState<unknown>>({
    ...defaultPageProps,
    ...pageProps,
    datadogProperty:
      pageProps?.datadogProperty ??
      datadogProperty ??
      parentPageState.datadogProperty,
    myHeartList: {
      product: new Set(pageProps?.myHeartList?.product || []),
      brand: new Set(pageProps?.myHeartList?.brand || []),
      contents: new Set(pageProps?.myHeartList?.contents || []),
    },
    myFollowList: {
      followIds: new Set(pageProps?.myFollowList?.followIds || []),
    },
  });

  const init = useCallback(async () => {
    const user = await getCookieWckAuthData();
    const basketCount = await getCookieBasketCount();
    const customAdYn = await getCustomAdAgreeYn();
    if (user?.userNo === '0') {
      setPageState(prev => ({
        ...prev,
        assistData: {
          basketCount,
          user: compress(JSON.stringify(user)),
          isLogin: user?.userNo !== '0',
          customAdYn,
        },
        myHeartList: {
          product: new Set([]),
          brand: new Set([]),
          contents: new Set([]),
        },
        myFollowList: {
          followIds: new Set([]),
        },
        initialData: pageProps?.initialData,
      }));
    } else {
      setPageState(prev => ({
        ...prev,
        assistData: {
          basketCount,
          user: compress(JSON.stringify(user)),
          isLogin: user?.userNo !== '0',
          customAdYn,
        },
        initialData: pageProps?.initialData,
      }));
    }
  }, [pageProps?.initialData]);

  useEffect(() => {
    if (!pageProps) return undefined;

    init();

    // bfcache에서 페이지 복원 시 assistData 갱신
    const handlePageShow = (event: PageTransitionEvent) => {
      init();
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        init();
      }
    };

    window.addEventListener('pageshow', handlePageShow);
    window.addEventListener('visibilitychange', handleVisibilityChange);

    window.addEventListener('beforeunload', () => {
      window.removeEventListener('pageshow', handlePageShow);
      window.removeEventListener('visibilitychange', handleVisibilityChange);
    });

    return () => {
      window.removeEventListener('pageshow', handlePageShow);
      window.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [pageProps]);

  const addMyHeartProductItem = useCallback((param: string) => {
    setPageState(prev => ({
      ...prev,
      myHeartList: {
        ...prev.myHeartList,
        product: new Set(prev.myHeartList.product).add(param),
      },
    }));
  }, []);

  const deleteMyHeartProdcutItem = useCallback((param: string) => {
    setPageState(prev => {
      const newSet = new Set(prev.myHeartList.product);
      newSet.delete(param);
      return {
        ...prev,
        myHeartList: {
          ...prev.myHeartList,
          product: newSet,
        },
      };
    });
  }, []);

  const addMyHeartBrandItem = useCallback((param: string) => {
    setPageState(prev => ({
      ...prev,
      myHeartList: {
        ...prev.myHeartList,
        brand: new Set(prev.myHeartList.brand).add(param),
      },
    }));
  }, []);

  const deleteMyHeartBrandItem = useCallback((param: string) => {
    setPageState(prev => {
      const newSet = new Set(prev.myHeartList.brand);
      newSet.delete(param);
      return {
        ...prev,
        myHeartList: {
          ...prev.myHeartList,
          brand: newSet,
        },
      };
    });
  }, []);

  const addMyHeartContentItem = useCallback(
    (param: { contentType: string; contentId: string }) => {
      setPageState(prev => ({
        ...prev,
        myHeartList: {
          ...prev.myHeartList,
          contents: prev.myHeartList.contents.add(param),
        },
      }));
    },
    []
  );

  const deleteMyHeartContentItem = useCallback(
    (param: { contentType: string; contentId: string }) => {
      setPageState(prev => {
        const newContents = new Set(
          Array.from(prev.myHeartList.contents).filter(
            item =>
              !(
                item.contentType === param.contentType &&
                item.contentId === param.contentId
              )
          )
        );
        return {
          ...prev,
          myHeartList: {
            ...prev.myHeartList,
            contents: newContents,
          },
        };
      });
    },
    []
  );

  const setContentsMyHeart = useCallback(
    (list: { contentType: string; contentId: string }[] = []) => {
      const newSet = new Set<{ contentType: string; contentId: string }>([]);
      list.forEach(id => newSet.add(id));
      setPageState(prev => ({
        ...prev,
        myHeartList: {
          ...prev.myHeartList,
          contents: newSet,
        },
      }));
    },
    []
  );

  const updateContentsMyHeart = useCallback(
    (list: { contentType: string; contentId: string }[] = []) => {
      const newSet = new Set(pageState.myHeartList.contents);
      list.forEach(id => newSet.add(id));
      setPageState(prev => ({
        ...prev,
        myHeartList: {
          ...prev.myHeartList,
          contents: newSet,
        },
      }));
    },
    [pageState.myHeartList.contents]
  );

  const setProductMyHeart = useCallback((list: string[] = []) => {
    setPageState(prev => {
      const newSet = new Set<string>([]);
      list.forEach(id => newSet.add(id));
      return {
        ...prev,
        myHeartList: {
          ...prev.myHeartList,
          product: newSet,
        },
      };
    });
  }, []);

  const updateProductMyHeart = useCallback((list: string[] = []) => {
    setPageState(prev => {
      const newSet = new Set(prev.myHeartList.product);
      list.forEach(id => newSet.add(id));
      return {
        ...prev,
        myHeartList: {
          ...prev.myHeartList,
          product: newSet,
        },
      };
    });
  }, []);

  const setBrandMyHeart = useCallback((list: string[] = []) => {
    setPageState(prev => {
      const newSet = new Set<string>([]);
      list.forEach(id => newSet.add(id));
      return {
        ...prev,
        myHeartList: {
          ...prev.myHeartList,
          brand: newSet,
        },
      };
    });
  }, []);

  const updateBrandMyHeart = useCallback((list: string[] = []) => {
    setPageState(prev => {
      const newSet = new Set(prev.myHeartList.brand);
      list.forEach(id => newSet.add(id));
      return {
        ...prev,
        myHeartList: {
          ...prev.myHeartList,
          brand: newSet,
        },
      };
    });
  }, []);

  const findContent = useCallback(
    (param: { contentId: string; contentType: string }) =>
      Array.from(pageState.myHeartList.contents).some(
        content =>
          content.contentId === param.contentId &&
          content.contentType === param.contentType
      ),
    [pageState.myHeartList.contents]
  );

  const setFollowIds = useCallback((list: number[] = []) => {
    setPageState(prev => {
      const newSet = new Set<number>([]);
      list.forEach(id => newSet.add(id));
      return {
        ...prev,
        myFollowList: {
          followIds: newSet,
        },
      };
    });
  }, []);

  const updateFollowIds = useCallback((list: number[] = []) => {
    setPageState(prev => {
      const newSet = new Set<number>(prev.myFollowList.followIds);
      list.forEach(id => newSet.add(id));
      return {
        ...prev,
        myFollowList: {
          followIds: newSet,
        },
      };
    });
  }, []);

  const addFollowId = useCallback((param: number) => {
    setPageState(prev => ({
      ...prev,
      myFollowList: {
        followIds: new Set(prev.myFollowList.followIds).add(param),
      },
    }));
  }, []);

  const deleteFollowId = useCallback((param: number) => {
    setPageState(prev => {
      const newSet = new Set(prev.myFollowList.followIds);
      newSet.delete(param);
      return {
        ...prev,
        myFollowList: {
          followIds: newSet,
        },
      };
    });
  }, []);

  const decodedUser = useMemo(
    () => JSON.parse(decompress(pageState.assistData.user)) as WckAuthCookie,
    [pageState.assistData.user]
  );
  const decodedAssistData = useMemo(
    () => ({ ...pageState.assistData, user: decodedUser }),
    [pageState.assistData, decodedUser]
  );

  const value = useMemo(
    () => ({
      ...pageState,
      decodedAssistData,
      updateContentsMyHeart,
      updateProductMyHeart,
      updateBrandMyHeart,
      addMyHeartProductItem,
      deleteMyHeartProdcutItem,
      addMyHeartBrandItem,
      deleteMyHeartBrandItem,
      addMyHeartContentItem,
      deleteMyHeartContentItem,
      setProductMyHeart,
      setBrandMyHeart,
      setContentsMyHeart,
      findContent,
      setFollowIds,
      updateFollowIds,
      addFollowId,
      deleteFollowId,
    }),
    [
      pageState,
      decodedAssistData,
      addMyHeartProductItem,
      deleteMyHeartProdcutItem,
      addMyHeartBrandItem,
      deleteMyHeartBrandItem,
      addMyHeartContentItem,
      deleteMyHeartContentItem,
      setProductMyHeart,
      updateProductMyHeart,
      setBrandMyHeart,
      updateBrandMyHeart,
      updateContentsMyHeart,
      setContentsMyHeart,
      findContent,
      setFollowIds,
      updateFollowIds,
      addFollowId,
      deleteFollowId,
    ]
  );

  return (
    <GlobalPagePropsContext.Provider value={value}>
      <>
        {pageProps && (
          <MarkeingScriptTags
            assistData={decodedAssistData}
            isLogin={pageState.assistData.isLogin}
            appInfo={pageState.appInfo}
          />
        )}
        {children}
      </>
    </GlobalPagePropsContext.Provider>
  );
}

export { GlobalPagePropsContext, GlobalPagePropsProvider };
